/*----------------------------------
- HOME: WORLD SCENES
----------------------------------*/

/**
 * Home's world pictures as exported images: the hero's two scenes (`HeroScene`), Day station and Night launch, which the
 * floating dock switches (Home v3, Gaetan, 2026-11-04), and the closing card's sunset outposts (`ClosingCard`). The
 * hero cuts are the mock's zoomed-out renders (docs/mockups/website-home-v3/hero-day-wide-1672x941.webp and
 * hero-night-wide-1672x941.webp): the station's base ends at half the picture's height. Phones get their own cut, the
 * exact window the mock's phone frame shows of the wide picture (lemlist's phone framing). The footer's night launch
 * picture is the frame's own (`components/layout/footerScene.ts`).
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0110
 * @rule The hero scene is Home's LCP: Day station is the server's picture (80 KB AVIF on a desktop, 40 KB on a phone);
 *       Night launch loads only when a visitor picks it in the dock.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { WorldPictureSources } from '@uniquedomains/components/marketing/hero-scene/WorldPicture';
import ClosingSunsetWebp from '@/client/assets/world/closing-outposts-sunset-1448x1086.webp';
import HeroDayWebp from '@/client/assets/world/hero-day-1672x940.webp';
import HeroDayPhoneWebp from '@/client/assets/world/hero-day-828x940.webp';
import HeroNightWebp from '@/client/assets/world/hero-night-1672x940.webp';
import HeroNightPhoneWebp from '@/client/assets/world/hero-night-828x940.webp';

/*----------------------------------
- TYPES
----------------------------------*/

/** The hero's two pictures, as the dock names them: `day` (Day station, the default) and `night` (Night launch). */
export type HomeHero = 'day' | 'night';

/*----------------------------------
- CONSTANTS
----------------------------------*/

// Why AVIF plus WebP: AVIF is about 20 to 30% lighter on these renders, which matters for the LCP; WebP is the
// fallback every browser without AVIF reads (`WorldPicture` puts it on the `img`).
//
// Why the AVIF files are plain public URLs and not imports: Proteum's asset rule
// (node_modules/proteum/cli/compiler/common/files/images.ts) covers bmp, gif, png, jpg, ico, svg and webp, not avif, so
// importing a `.avif` fails the build. Until the framework adds it, the AVIF cuts live in `apps/website/public/assets/
// world/` (served at `/public/assets/world/`). `?v=` marks them versioned for Proteum's public cache
// (`isVersionedPublicAssetRequest`): bump it whenever a file is exported again, since the file name does not change.
//
// The recipe of 06bad0cd5, on 2026-11-04: ffmpeg crops each mock file to 1672 x 940 (its last row dropped: Chromium
// decodes an odd-height AVIF from `sips` as a fully transparent picture), `sips -s format avif -s formatOptions 60` for
// the AVIF, and the WebP re-encoded at quality 0.8 in a Chromium canvas (this machine's ffmpeg has no WebP encoder and
// sips only reads WebP): 80 KB and 60 KB AVIF, 106 KB and 80 KB WebP, against the mock's 193 KB and 157 KB.
const AVIF_VERSION = '20261104';
const avifUrl = (name: string): string => `/public/assets/world/${name}.avif?v=${AVIF_VERSION}`;

// The phone cut: the mock draws the wide picture at 202vw shifted 49.3vw left (`background-size: 202vw auto;
// background-position: -49.3vw 0`), so a phone shows its columns 408 to 1236 (1672 x 49.3 / 202 = 408, 1672 x 100 / 202
// = 828 wide) over the full height. Cut once with `ffmpeg -vf crop=828:940:408:0` from the mock's file (both sides even,
// see the AVIF note above), the phone layer draws it at the window's full width from the top (home.css), the same frame
// as the mock at a third of the pixels: 40 and 35 KB AVIF, 55 and 48 KB WebP. A phone never loads the wide cut.

/** The hero's sky per dock choice; `day` is Home's LCP, loaded eagerly at high priority by `HeroScene`. */
export const HOME_HERO_SCENES: Record<HomeHero, WorldPictureSources> = {
    day: {
        desktop: { avif: avifUrl('hero-day-1672x940'), webp: HeroDayWebp, width: 1672, height: 940 },
        phone: { avif: avifUrl('hero-day-828x940'), webp: HeroDayPhoneWebp, width: 828, height: 940 },
    },
    night: {
        desktop: { avif: avifUrl('hero-night-1672x940'), webp: HeroNightWebp, width: 1672, height: 940 },
        phone: { avif: avifUrl('hero-night-828x940'), webp: HeroNightPhoneWebp, width: 828, height: 940 },
    },
};

/**
 * The closing card's picture: the investor's market post and the founder's habitat at sunset, on the card's bottom edge
 * (docs/mockups/website-home-v3/closing-outposts-sunset-1448x1086.webp; 33 KB AVIF, 46 KB WebP). Lazy, far below the fold.
 */
export const HOME_CLOSING_SCENE: WorldPictureSources = {
    desktop: { avif: avifUrl('closing-outposts-sunset-1448x1086'), webp: ClosingSunsetWebp, width: 1448, height: 1086 },
};

/*----------------------------------
- EXPORTS
----------------------------------*/

export default HOME_HERO_SCENES;
